'use client'

import { Locked } from '@carbon/icons-react'

import { cn } from '@/lib/utils'

/* Stacked browser windows — one chrome per tenant, receding upward. Used to
   show that the same platform serves many institutions on their own domains,
   which a single frame cannot say.

   Each layer downshifts in scale (radius, padding, type, buttons) rather than
   being CSS-scaled, so the chrome of the small ones stays crisp instead of
   going blurry at the edges. */

const LAYERS = [
  {
    radius: 11,
    py: 11,
    px: 17,
    barH: 22,
    fontSize: 16,
    dot: 11,
    gap: 6,
    icon: 17,
  },
  {
    radius: 10,
    py: 9.5,
    px: 14,
    barH: 19,
    fontSize: 13,
    dot: 9.5,
    gap: 6,
    icon: 14,
  },
  {
    radius: 8,
    py: 8.5,
    px: 12,
    barH: 17,
    fontSize: 10.5,
    dot: 8,
    gap: 3,
    icon: 12,
  },
  {
    radius: 7,
    py: 7,
    px: 10,
    barH: 14,
    fontSize: 8,
    dot: 7,
    gap: 3,
    icon: 9.5,
  },
] as const

/** Horizontal inset per layer, so each window behind sits proud of the front. */
const INSET = 44

/* Real window-button colours on the front pane only; the ones behind go grey,
   the way an unfocused window does. */
const TRAFFIC = ['#ff5f57', '#febc2e', '#28c840'] as const

export function BrowserStack({
  domains = [],
  className,
}: {
  domains?: string[]
  className?: string
}) {
  const list = domains.slice(0, LAYERS.length)

  return (
    <div className={cn('flex w-full items-center justify-center', className)}>
      <div
        role="img"
        aria-label={`Stacked browser windows for ${list.join(', ')}`}
        className="flex w-full max-w-lg flex-col-reverse items-center justify-center"
      >
        {list.map((domain, i) => {
          const l = LAYERS[i]!
          return (
            <div
              key={domain}
              aria-hidden
              className="relative flex w-full items-center border border-border/70 bg-muted dark:bg-muted/60"
              style={{
                width: `calc(100% - ${INSET * i}px)`,
                borderRadius: `${l.radius}px ${l.radius}px 0 0`,
                padding: `${l.py}px ${l.px}px`,
                zIndex: 10 - i,
                opacity: 1 - i * 0.12,
                // Panes behind pull down to close the seam against the pane in
                // front. The front pane gets none, so the stack's bottom edge
                // is exactly its bottom border — it can then sit flush on a
                // rule instead of overhanging it by the overlap amount.
                marginBottom: i === 0 ? 0 : -3,
              }}
            >
              <div className="flex items-center" style={{ gap: `${l.gap}px` }}>
                {[0, 1, 2].map((d) => (
                  <span
                    key={d}
                    className="shrink-0 rounded-full"
                    style={{
                      width: `${l.dot}px`,
                      height: `${l.dot}px`,
                      backgroundColor:
                        i === 0
                          ? TRAFFIC[d]
                          : 'hsl(var(--muted-foreground) / 0.3)',
                    }}
                  />
                ))}
              </div>

              <div
                className="mx-auto flex items-center justify-center gap-1 text-foreground"
                style={{ height: `${l.barH}px`, fontSize: `${l.fontSize}px` }}
              >
                <Locked
                  size={l.icon}
                  className="shrink-0 text-muted-foreground"
                  style={{ width: `${l.icon}px`, height: `${l.icon}px` }}
                />
                <span className="truncate font-mono">{domain}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default BrowserStack
